import {createSelector} from 'reselect';
import {Iterable} from 'immutable';

import {getReviews, getTranslations, getAbacusTests, getTravelerFeatures} from './store';
import {getOwnerDetails} from './ownerDetails';
import {getBearStayReviewData, getBookingType, getDetailPageUrl} from './listing';
import {getDeviceSize, getIsMobile, getIsDesktop, getLocaleJS, getReviewPageSize} from './system';
import {labelMaker} from '../../server/utils/labelMaker';
import {isReviewsModalOpen} from './modalStates';
import {convertDashesToSlashes} from '../utils/dateHelper';
import {isInBucketData} from '../utils/abacusHelper';

import DateTimeFormatCache from '../utils/dateTimeFormatCache';

const REVIEW_COUNT_MIN = 5;
const arrivalDateFormatterCache = new DateTimeFormatCache({
    year: 'numeric',
    month: 'short'
});
const createdDateFormatterCache = new DateTimeFormatCache({
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});

const getJsReviews = createSelector(
    [getReviews],
    (reviewsData) => {
        return Iterable.isIterable(reviewsData) ? reviewsData.toJS() : reviewsData;
    }
);

const getBearStayBannerLabels = createSelector(
    [getTranslations],
    (trans) => {
        return {
            title: trans.bearStayNewTitle,
            description: trans.bearStayTeaserMessage,
            readReview: trans.bearStayTeaserButton
        };
    }
);

const getBearStayReview = createSelector(
    [getTranslations, getBearStayReviewData, getIsDesktop, getDeviceSize],
    (trans, bearStayReview, isDesktop, deviceSize) => {
        if (!bearStayReview) {
            return null;
        }

        let date = bearStayReview.stayedDate;
        const dateObj = new Date(date.split('T')[0].replace(/-/g, '/'));
        const splittedDate = dateObj.toDateString().split(' ');
        date = `${splittedDate[1]} ${splittedDate[2]}, ${splittedDate[3]}`;
        const content = bearStayReview.stayedComments;

        return {
            labels: {
                content,
                date,
                more: 'more',
                footerText: trans.bearStayTheBear,
                title: trans.bearStayReviewTitle,
                tooltipLabelText: trans.bearStayHelpLink
            },
            tooltipOptions: {
                delayShow: isDesktop,
                tooltipCentered: true,
                tooltipContent: trans.bearStayHelpContent,
                tooltipLabelText: trans.bearStayHelpLink,
                tooltipPlacement: isDesktop || deviceSize === 'md' ? 'left' : 'top',
                tooltipType: 'tooltip--lg'
            }
        };
    }
);

const getReviewsLabels = createSelector(
    [getJsReviews, getOwnerDetails, getTranslations],
    (reviews, owner, trans) => {
        const viewAllReviews = reviews.reviewCount
            ? labelMaker(trans.viewAllReviews, reviews.reviewCount)
            : '';
        const seeAllReviews = reviews.reviewCount
            ? labelMaker(trans.seeAllReviews, reviews.reviewCount)
            : '';
        const labels = {
            nextPageOfReviews: trans.nextPageOfReviews,
            previousPageOfReviews: trans.previousPageOfReviews,
            reviews: labelMaker(trans.reviewCount, {
                REVIEWCOUNT: reviews.reviewCount
            }),
            ownershipTransferred: trans.ownershipTransferred,
            sortBy: trans.sortBy,
            sortByRecentMost: trans.sortByRecentMost,
            sortByRecentLeast: trans.sortByRecentLeast,
            filterBy: trans.filterBy,
            noFilter: trans.noFilter,
            filterByFiveStar: trans.filterByFiveStar,
            filterByFourStar: trans.filterByFourStar,
            filterByThreeStar: trans.filterByThreeStar,
            filterByTwoStar: trans.filterByTwoStar,
            filterByOneStar: trans.filterByOneStar,
            filterByVerifiedReview: trans.filterByVerifiedReview,
            filterByWithVerifiedReview: trans.filterByWithVerifiedReview,
            filterByWithoutVerifiedReview: trans.filterByWithoutVerifiedReview,
            averageRatingTitle: trans.averageRatingTitle,
            averageRating: labelMaker(trans.averageRatingText, {owner_first_name: owner.name}),
            totalReviews: trans.totalReviews,
            pagesize: trans.pagesize,
            latestReviews: trans.latestReviews,
            tenByPage: trans.tenByPage,
            twentyByPage: trans.twentyByPage,
            fiftyByPage: trans.fiftyByPage,
            of: trans.of,
            emptyReviews: trans.emptyReviews,
            by: trans.by,
            verifiedStay: trans.verifiedStay,
            submitted: trans.submitted,
            stayed: trans.stayed,
            source: trans.source,
            isHelpful: trans.isHelpful,
            yes: trans.yes,
            no: trans.no,
            more: trans.viewMore,
            less: trans.viewLess,
            readMore: trans.viewMore,
            ownersResponse: trans.ownersResponse,
            viewAllReviews,
            seeAllReviews,
            reportReviewActive: trans.reviewsReportLinkActive,
            reportReviewInactive: trans.reviewsReportLinkInactive,
            reviewsLegalDisclaimer: trans.reviewsLegalDisclaimer
        };
        return labels;
    }
);

const getReviewList = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.reviews;
    }
);

const getFormattedReviews = createSelector(
    [getReviewList, getLocaleJS, getTravelerFeatures],
    (reviewList, locale, travelerFeatures) => {
        const formattedReviews = reviewList.map((review) => {
            // review.arrivalDate & review.datePublished dates come as ISO string "2017-04-20T00:00:00Z"
            const arrivalDate = new Date(convertDashesToSlashes(review.arrivalDate.split('T')[0]));
            const createdDate = new Date(
                convertDashesToSlashes(review.datePublished.split('T')[0])
            );
            const arrivalDateFormatter = arrivalDateFormatterCache.getFormatter(locale);
            const createdDateFormatter = createdDateFormatterCache.getFormatter(locale);
            const formattedArrivalDate = arrivalDateFormatter.format(arrivalDate);
            const formattedCreatedDate = createdDateFormatter.format(createdDate);
            const {isReportReviewActive = true} = review;

            return Object.assign(
                {},
                review,
                {arrivalDate: formattedArrivalDate},
                {createdDate: formattedCreatedDate},
                travelerFeatures.hasReportReviewLink ? {isReportReviewActive} : {}
            );
        });
        return formattedReviews;
    }
);

const getReviewSummaryData = createSelector(
    [getJsReviews],
    (reviewsData) => {
        // Translate the data received from the API to the contract
        // our components need.
        const {
            averageRating,
            oneStarReviewCount,
            twoStarReviewCount,
            threeStarReviewCount,
            fourStarReviewCount,
            fiveStarReviewCount
        } = reviewsData;

        return {
            score: averageRating,
            totalOne: oneStarReviewCount,
            totalTwo: twoStarReviewCount,
            totalThree: threeStarReviewCount,
            totalFour: fourStarReviewCount,
            totalFive: fiveStarReviewCount
        };
    }
);

const getNumReviewsToShow = createSelector(
    [getIsMobile, getReviewPageSize, isReviewsModalOpen],
    (isMobile, reviewPageSize, reviewsModalOpen) => {
        const MOBILE_PREVIEW_COUNT = 3;

        if (isMobile && !reviewsModalOpen) {
            return MOBILE_PREVIEW_COUNT;
        }

        return reviewPageSize;
    }
);

const getWriteReviewLinkData = createSelector(
    [getBookingType, getDetailPageUrl, getTranslations, getTravelerFeatures],
    (bookingType, detailPageUrl, trans, travelerFeatures) => {
        if (!bookingType.payPerBooking && travelerFeatures.hasWriteReviews) {
            const splitDetailPageUrl = detailPageUrl.split('/');
            const index = splitDetailPageUrl.length === 2 ? 1 : 2;
            splitDetailPageUrl.splice(index, 0, 'reviews/write');

            return {
                href: splitDetailPageUrl.join('/'),
                label: trans.writeReview
            };
        }

        return null;
    }
);

const getButtonVisible = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.isTranslationButtonVisible;
    }
);

const getOwnerRating = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.ownerRating || {};
    }
);

/* Average Rating AB Testing */
const getMayShowAverageRating = createSelector(
    [getJsReviews],
    (reviewsData) => {
        const {reviewCount, ownerRating} = reviewsData;
        return !reviewCount && !!(ownerRating || {}).reviewCount;
    }
);

const getAbTestOwnerRating = createSelector(
    [getOwnerRating, getAbacusTests],
    (ownerRating, abacusTestData) => {
        const isBucketed = isInBucketData(abacusTestData, 'ha_pdp_avg_reviews_web', 1);

        return isBucketed && ownerRating;
    }
);

const getReviewsSelector = createSelector(
    [
        getReviewsLabels,
        getMayShowAverageRating,
        getAbTestOwnerRating,
        getReviewSummaryData,
        getJsReviews,
        getFormattedReviews,
        getNumReviewsToShow,
        getIsMobile,
        getBearStayReview,
        getTravelerFeatures,
        getWriteReviewLinkData,
        getButtonVisible,
        getLocaleJS
    ],
    (
        labels,
        mayShowAverageRating,
        ownerRating,
        reviewsSummaryData,
        reviewsData,
        formattedReviews,
        numReviewsToShow,
        isMobile,
        bearStayReview,
        travelerFeatures,
        writeReviewLinkData,
        isTranslationButtonVisible,
        locale
    ) => {
        const page = reviewsData.pageIndex;
        const pageSize = numReviewsToShow;
        const totalReviewCount = reviewsData.reviewCount;
        const pageStartIndex = (page - 1) * pageSize;
        const pageEndIndex =
            page * pageSize <= totalReviewCount ? page * pageSize : totalReviewCount;
        const reviewsStart = pageStartIndex === 0;
        const reviewsEnd = pageEndIndex === totalReviewCount;
        const reviewsListForSinglePage = formattedReviews.slice(pageStartIndex, pageEndIndex);
        const reviewListForPreview = formattedReviews.slice(0, numReviewsToShow);
        const reviewsRemainder = formattedReviews.length % pageSize;
        const reviewListAllLoadedReviews =
            formattedReviews.length > pageSize && reviewsRemainder !== 0
                ? formattedReviews.slice(0, formattedReviews.length - reviewsRemainder)
                : formattedReviews;
        const hasMoreReviews = numReviewsToShow < totalReviewCount;
        const fetchedAllReviews = formattedReviews.length === totalReviewCount;
        const showReportLinks = travelerFeatures.hasReportReviewLink;
        const showReviewsLegalDisclaimer = travelerFeatures.showReviewsLegalDisclaimer;
        return {
            labels,
            ownerRating,
            mayShowAverageRating,
            reviewsSummaryData,
            page,
            hasMoreReviews,
            isFetching: reviewsData.isFetching,
            totalReviewCount,
            reviewsStart,
            reviewsEnd,
            reviewsListForSinglePage,
            reviewListForPreview,
            reviewListAllLoadedReviews,
            pageStartIndex: pageStartIndex + 1,
            pageEndIndex,
            isMobile,
            pageSize,
            fetchedAllReviews,
            reviewCountMin: REVIEW_COUNT_MIN,
            bearStayReview,
            showReportLinks,
            showReviewsLegalDisclaimer,
            writeReviewLinkData,
            isTranslationButtonVisible,
            locale
        };
    }
);

export default getReviewsSelector;
export {
    getBearStayBannerLabels,
    getBearStayReview,
    getReviewsLabels,
    getReviewsSelector,
    getReviewList,
    getFormattedReviews,
    getReviewSummaryData,
    getNumReviewsToShow,
    getJsReviews,
    getWriteReviewLinkData
};
